import { ThemeEvents } from '@theme/events';

/**
 * Seasons — Caja Diseño Seasons en el carrito.
 *
 * REGLA DE NEGOCIO
 *   - La caja se añade SIEMPRE que el carrito lleve algo que vaya dentro de
 *     una caja a medida.
 *   - Su PRECIO no lo decide este módulo: lo deciden los descuentos
 *     automáticos del admin — 1,95 € por debajo de 25 € y gratis desde 25 €
 *     o con un pack. Aquí no se calculan importes.
 *   - Excepción única: un carrito compuesto sólo de wax melts, velas u
 *     outlet no lleva caja.
 *
 * POR EXCLUSIÓN, NO POR INCLUSIÓN
 * `collection.products` devuelve como mucho 50 productos en Liquid y
 * caja-regalo-personalizada tiene 75: una lista de inclusión dejaría fuera a
 * 25 productos que nunca dispararían la caja. Por eso el tema envía las
 * variantes EXCLUIDAS (wax melts, velas, outlet y cajas ya preparadas), que
 * caben todas por debajo del tope, y aquí se considera "de caja a medida"
 * todo lo que no esté en esa lista.
 *
 * POR QUÉ NO PARCHEA `fetch`
 * La versión original interceptaba `window.fetch`. Otra app del storefront
 * vuelve a parchearlo y dejaba huérfano el parche, así que la caja no se
 * añadía nunca — verificado en producción. Ahora es un reconciliador sobre
 * el evento `cart:update` del tema: nadie puede dejarlo fuera de la cadena.
 */

const CFG = typeof Theme !== 'undefined' ? Theme?.premiumBox : null;

/** Marca la línea como puesta por nosotros y no por el cliente. */
const MARK = '_Obligatorio';

/** Nuestras propias escrituras disparan `cart:update`: evita reentrar. */
let busy = false;
/**
 * Llegó otro `cart:update` mientras se conciliaba. Antes se descartaba: si el
 * cliente añadía dos cosas seguidas (una vela y luego una galleta, por
 * ejemplo) la segunda no se miraba y la caja podía no aparecer.
 */
let pending = false;
/** Firma del último carrito ya conciliado. */
let lastSignature = '';

function routes() {
  try {
    return typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : {};
  } catch {
    return {};
  }
}

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

async function getCart() {
  const res = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

/** @param {{items: Array<any>}} cart */
const signature = (cart) => cart.items.map((i) => `${i.variant_id}:${i.quantity}`).join('|');

/**
 * ¿Hay algo en el carrito que vaya dentro de una caja a medida?
 * @param {{items: Array<any>}} cart
 * @param {Set<number>} excluded
 * @param {number} boxVariantId
 */
function needsBox(cart, excluded, boxVariantId) {
  return cart.items.some(
    (item) => item.variant_id !== boxVariantId && !excluded.has(item.variant_id)
  );
}

async function addBox(boxVariantId) {
  await fetch(routes().cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ items: [{ id: boxVariantId, quantity: 1, properties: { [MARK]: 'Incluido' } }] }),
  });
}

/** @param {string} key clave de línea del carrito */
async function removeLine(key) {
  await fetch(routes().cart_change_url || '/cart/change.js', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ id: key, quantity: 0 }),
  });
}

/**
 * Deja el carrito coherente con la regla. Idempotente.
 * @param {{variantId: number, excludedVariantIds: Array<number>}} cfg
 */
async function reconcile(cfg) {
  if (busy) {
    pending = true;
    return;
  }
  busy = true;
  pending = false;
  try {
    const cart = await getCart();
    const sig = signature(cart);
    if (sig === lastSignature) return;

    const excluded = new Set(cfg.excludedVariantIds);
    const boxLine = cart.items.find((i) => i.variant_id === cfg.variantId);
    const wants = cart.items.length > 0 && needsBox(cart, excluded, cfg.variantId);

    // La caja la añadió el cliente a mano desde su ficha: no la tocamos.
    if (boxLine && !(boxLine.properties && boxLine.properties[MARK])) {
      lastSignature = sig;
      return;
    }

    if (wants && !boxLine) {
      await addBox(cfg.variantId);
    } else if (!wants && boxLine) {
      await removeLine(boxLine.key);
    } else if (boxLine && boxLine.quantity > 1) {
      // Sólo una caja por pedido.
      await fetch(routes().cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ id: boxLine.key, quantity: 1 }),
      });
    } else {
      lastSignature = sig;
      return;
    }

    const updated = await getCart();
    lastSignature = signature(updated);
    updateCartBubble(updated.item_count);
    document.dispatchEvent(new CustomEvent('seasons:premium-box-reconciled', { bubbles: true }));
    // Si el carrito estaba ocupado y no se repintó a la primera, se repite.
    const boxInCart = updated.items.some((i) => i.variant_id === cfg.variantId);
    setTimeout(() => {
      const lists = document.querySelectorAll('cart-items-component');
      const stale = [...lists].some(
        (list) =>
          list.querySelector('form') !== null &&
          !!list.querySelector(`a.cart-items__title[href*="variant=${cfg.variantId}"]`) !== boxInCart
      );
      if (stale) {
        document.dispatchEvent(new CustomEvent('seasons:premium-box-reconciled', { bubbles: true }));
      }
    }, 1200);
  } catch (err) {
    console.error('Seasons — no se pudo conciliar la Caja Diseño:', err);
  } finally {
    busy = false;
    if (pending) {
      pending = false;
      reconcile(cfg);
    }
  }
}

/**
 * El contador del icono del carrito no se enteraba de la caja que añadimos
 * nosotros y se quedaba una unidad por debajo.
 * @param {number} count
 */
function updateCartBubble(count) {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    try {
      /** @type {any} */ (icon).renderCartBubble?.(count, false, false);
    } catch {
      /* el icono es opcional */
    }
  });
}

function start() {
  if (!CFG || !CFG.enabled) return;
  if (!CFG.variantId || !Array.isArray(CFG.excludedVariantIds)) return;

  const cfg = { variantId: CFG.variantId, excludedVariantIds: CFG.excludedVariantIds };

  document.addEventListener(ThemeEvents.cartUpdate, () => reconcile(cfg));

  // Primera pasada: cubre recargas y carritos heredados de otra sesión.
  reconcile(cfg);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}

export default start;
